import { useState, useMemo } from 'react';
import { Lock, User, Phone, MapPin, Building, Eye, EyeOff, AlertCircle, Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';

export default function OnboardingModal() {
  const { user, changePassword, updateBillingInfo, mustChangePassword } = useAuth();
  
  // Calculate what's needed BEFORE any hooks
  const billing = user?.billing_info || {};
  const needsPassword = mustChangePassword;
  const needsBilling = !billing.first_name || !billing.last_name || !billing.address || !billing.city || !billing.postal_code || !billing.phone;
  
  // All hooks must be called unconditionally
  const [currentStep, setCurrentStep] = useState(needsPassword ? 'password' : 'billing');
  const [passwordDone, setPasswordDone] = useState(!needsPassword);
  const [billingDone, setBillingDone] = useState(!needsBilling);
  
  // Password state
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirm: ''
  });
  
  // Billing state
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [billingData, setBillingData] = useState({
    first_name: billing.first_name || '',
    last_name: billing.last_name || '',
    company_name: billing.company_name || '',
    siret: billing.siret || '',
    address: billing.address || '',
    city: billing.city || '',
    postal_code: billing.postal_code || '',
    country: billing.country || 'France',
    phone: billing.phone || ''
  });

  // Memoize initial values needed check
  const showModal = useMemo(() => {
    return needsPassword || needsBilling;
  }, [needsPassword, needsBilling]);

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    setPasswordError('');
  };

  const handleBillingChange = (e) => {
    setBillingData({ ...billingData, [e.target.name]: e.target.value });
    setBillingError('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');

    if (passwordData.new_password !== passwordData.new_password_confirm) {
      setPasswordError('Les mots de passe ne correspondent pas');
      setPasswordLoading(false);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caractères');
      setPasswordLoading(false);
      return;
    }

    try {
      await changePassword(
        passwordData.current_password,
        passwordData.new_password,
        passwordData.new_password_confirm
      );
      setPasswordDone(true);
      if (needsBilling) {
        setCurrentStep('billing');
      }
    } catch (err) {
      setPasswordError(err.message || 'Erreur lors du changement de mot de passe');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    setBillingLoading(true);
    setBillingError('');

    // Validate required fields
    if (!billingData.first_name || !billingData.last_name || !billingData.address || 
        !billingData.city || !billingData.postal_code || !billingData.phone) {
      setBillingError('Veuillez remplir tous les champs obligatoires');
      setBillingLoading(false);
      return;
    }

    try {
      await updateBillingInfo(billingData);
      setBillingDone(true);
    } catch (err) {
      setBillingError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setBillingLoading(false);
    }
  };

  // Calculate progress for UI
  const totalSteps = (needsPassword ? 1 : 0) + (needsBilling ? 1 : 0);
  
  // Don't render if nothing needed or all done
  if (!showModal || (passwordDone && billingDone)) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="onboarding-modal">
      <div className="bg-card border border-border rounded-2xl max-w-lg w-full shadow-2xl animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-bold mb-1">Bienvenue sur votre espace client</h2>
          <p className="text-muted-foreground text-sm">
            Pour continuer, veuillez compléter les informations suivantes
          </p>
          
          {/* Progress indicator */}
          {totalSteps > 1 && (
            <div className="flex items-center gap-2 mt-4">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                passwordDone ? 'bg-green-500 text-white' : currentStep === 'password' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {passwordDone ? <Check className="h-4 w-4" /> : '1'}
              </div>
              <div className={`flex-1 h-1 rounded ${passwordDone ? 'bg-green-500' : 'bg-muted'}`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                billingDone ? 'bg-green-500 text-white' : currentStep === 'billing' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {billingDone ? <Check className="h-4 w-4" /> : '2'}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Password Step */}
          {currentStep === 'password' && !passwordDone && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-900/30">
                  <Lock className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Changement de mot de passe</h3>
                  <p className="text-sm text-muted-foreground">Obligatoire pour sécuriser votre compte</p>
                </div>
              </div>

              {passwordError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {passwordError}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Mot de passe actuel (provisoire)</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="current_password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    required
                    className="pl-10 pr-10"
                    placeholder="Votre mot de passe actuel"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Nouveau mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="new_password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    className="pl-10 pr-10"
                    placeholder="Minimum 8 caractères"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Confirmer le nouveau mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="new_password_confirm"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.new_password_confirm}
                    onChange={handlePasswordChange}
                    required
                    className="pl-10"
                    placeholder="Confirmez le mot de passe"
                  />
                </div>
              </div>

              <Button type="submit" disabled={passwordLoading} className="w-full btn-primary">
                {passwordLoading ? 'Modification...' : 'Valider le mot de passe'}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          )}

          {/* Billing Step */}
          {currentStep === 'billing' && !billingDone && (
            <form onSubmit={handleBillingSubmit} className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Informations de facturation</h3>
                  <p className="text-sm text-muted-foreground">Nécessaires pour vos devis et factures</p>
                </div>
              </div>

              {billingError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {billingError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Prénom *</label>
                  <Input
                    name="first_name"
                    value={billingData.first_name}
                    onChange={handleBillingChange}
                    required
                    placeholder="Jean"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Nom *</label>
                  <Input
                    name="last_name"
                    value={billingData.last_name}
                    onChange={handleBillingChange}
                    required
                    placeholder="Dupont"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Téléphone *
                </label>
                <Input
                  name="phone"
                  type="tel"
                  value={billingData.phone}
                  onChange={handleBillingChange}
                  required
                  placeholder="06 12 34 56 78"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Adresse *
                </label>
                <Input
                  name="address"
                  value={billingData.address}
                  onChange={handleBillingChange}
                  required
                  placeholder="123 rue de la Paix"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Code postal *</label>
                  <Input
                    name="postal_code"
                    value={billingData.postal_code}
                    onChange={handleBillingChange}
                    required
                    placeholder="75001"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Ville *</label>
                  <Input
                    name="city"
                    value={billingData.city}
                    onChange={handleBillingChange}
                    required
                    placeholder="Paris"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Pays</label>
                <Input
                  name="country"
                  value={billingData.country}
                  onChange={handleBillingChange}
                  placeholder="France"
                />
              </div>

              <div className="pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
                  <Building className="h-3 w-3" />
                  Informations entreprise (optionnel)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Société</label>
                    <Input
                      name="company_name"
                      value={billingData.company_name}
                      onChange={handleBillingChange}
                      placeholder="Ma Société SARL"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">SIREN</label>
                    <Input
                      name="siret"
                      value={billingData.siret}
                      onChange={handleBillingChange}
                      placeholder="123 456 789"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                {needsPassword && passwordDone && (
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setCurrentStep('password')}
                    className="flex-shrink-0"
                  >
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Retour
                  </Button>
                )}
                <Button type="submit" disabled={billingLoading} className="flex-1 btn-primary">
                  {billingLoading ? 'Enregistrement...' : 'Terminer'}
                  <Check className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
